import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Instagram, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-[#0a0a0a] border-t border-[#333333] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="flex flex-col gap-4">
            <Link to="/" className="flex items-center gap-2" data-testid="footer-logo">
              <Shield className="w-8 h-8 text-[#FF0099]" />
              <span className="font-heading text-xl font-bold">
                <span className="text-white">FIGHT LIKE A </span>
                <span className="text-[#FF0099]">GYAL</span>
              </span>
            </Link>
            <p className="text-[#A3A3A3] text-sm">
              {t('footer.rights')}
              <br />
              <span className="font-accent text-[#FAFF00] text-xs">{t('footer.tagline')}</span>
            </p>
            {/* Instagram */}
            <a 
              href="https://instagram.com/fightlikeagyal" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#FF0099] transition-colors"
              data-testid="social-instagram"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-sm">@fightlikeagyal</span>
            </a>
            {/* Email */}
            <a 
              href="mailto:fightlikeagyal@gmail.com"
              className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#FF0099] transition-colors"
              data-testid="contact-email"
            >
              <Mail className="w-5 h-5" />
              <span className="text-sm">fightlikeagyal@gmail.com</span>
            </a>
          </div>

          {/* Links */}
          <div className="flex flex-col gap-4">
            <h3 className="font-heading text-white font-bold uppercase tracking-wider">
              Quick Links
            </h3>
            <div className="flex flex-col gap-2">
              <Link 
                to="/shop" 
                className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors text-sm"
                data-testid="footer-shop-link"
              >
                Shop All
              </Link>
              <Link 
                to="/about" 
                className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors text-sm"
                data-testid="footer-about-link"
              >
                About Us
              </Link>
              <Link 
                to="/faq" 
                className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors text-sm"
                data-testid="footer-faq-link"
              >
                FAQ
              </Link>
              <Link 
                to="/shipping" 
                className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors text-sm"
                data-testid="footer-shipping-link"
              >
                Shipping
              </Link>
              <Link 
                to="/terms" 
                className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors text-sm"
                data-testid="footer-terms-link"
              >
                Terms & Conditions
              </Link>
              <Link 
                to="/privacy" 
                className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors text-sm"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
            </div>
          </div>

          {/* PayPal Trust Badge */}
          <div className="flex flex-col gap-4">
            <h3 className="font-heading text-white font-bold uppercase tracking-wider">
              Secure Payment
            </h3>
            <div className="bg-[#1A1A1A] border border-[#333333] p-4 rounded">
              <div className="flex items-center gap-3 mb-3">
                {/* Official PayPal Logo */}
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="20" viewBox="0 0 124 33">
                  <path fill="#253B80" d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zM47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z"/>
                  <path fill="#179BD7" d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z"/>
                </svg>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-[#00FFFF]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                <span className="text-[#00FFFF] text-sm font-bold">Buyer Protection</span>
              </div>
              <p className="text-[#666666] text-xs">
                {t('footer.paypalProtection')}
              </p>
            </div>
          </div>

          {/* Legal Notice */}
          <div className="flex flex-col gap-4">
            <h3 className="font-heading text-white font-bold uppercase tracking-wider">
              {t('footer.legalNotice')}
            </h3>
            <p className="text-[#666666] text-xs leading-relaxed">
              {t('footer.disclaimer')}
            </p>
            <p className="text-[#666666] text-xs leading-relaxed">
              {t('footer.legalFrance')}
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-[#333333] text-center">
          <p className="text-[#666666] text-xs">
            © {new Date().getFullYear()} Fight like a GYAL. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
